import { useState } from "react"
import { Timer } from "lucide-react"
import { useStalenessTick } from "@/hooks/useStalenessTick"
import { cn } from "@/lib/utils"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useTranslation } from "react-i18next"
import { getIntlLocale } from "@/lib/i18n/format"

type Props = {
  proximityStatus?: string
  stationarySince?: string
}

function StationaryTooltip({ since }: { since: number }) {
  const { t } = useTranslation()
  const now = useStalenessTick()
  const [open, setOpen] = useState(false)
  const minutes = Math.max(0, Math.floor((now - since) / 60_000))
  const duration =
    minutes >= 60
      ? `${Math.floor(minutes / 60)}h ${minutes % 60}m`
      : `${minutes}m`

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={t("maps.stationaryIndicator.ariaLabel")}
            className={cn(
              "inline-flex shrink-0 items-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              minutes >= 120
                ? "text-red-600 dark:text-red-400"
                : minutes >= 60
                  ? "text-yellow-600 dark:text-yellow-400"
                  : "text-muted-foreground hover:text-foreground",
            )}
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              setOpen((value) => !value)
            }}
          >
            <Timer className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <div>{t("maps.stationaryIndicator.stationaryFor", { duration })}</div>
          <div>
            {t("maps.stationaryIndicator.since", {
              date: new Date(since).toLocaleString(getIntlLocale(undefined), {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              }),
            })}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export default function StationaryIndicator({
  proximityStatus,
  stationarySince,
}: Props) {
  const since = stationarySince ? Date.parse(stationarySince) : NaN
  if (
    proximityStatus !== "arrived" ||
    !Number.isFinite(since) ||
    since > Date.now()
  ) {
    return null
  }

  return <StationaryTooltip key={stationarySince} since={since} />
}
